import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { SlideoutLayout } from '@jbhi-fi/components/src/Attach/SlidoutLayout/SlideoutLayout';
import { SlideoutLayoutSection } from '@jbhi-fi/components/src/Attach/SlideoutLayoutSection/SlideoutLayoutSection';
import React from 'react';
import { useSuggestedProducts } from '@jbhi-fi/components/src/hooks/useSuggestedProducts';
import { SlideoutProductsModule } from '@jbhi-fi/components/src/Attach/SlideoutProductsModule/SlideoutProductsModule';
import { AttachInfoTransformer } from '@jbhi-fi/components/src/Contentful/transformers/AttachInfoTransformer';
import { AttachBundleTransformer } from '@jbhi-fi/components/src/Contentful/transformers/AttachBundleTransformer';
import { fetchServiceSuggestions, getSelectedServiceSuggestion } from '../pdp/utils/product-service';
import ProductService from '../pdp/productService/ProductService';
import { LoadingSkeleton } from '@jbhi-fi/components/src/LoadingSkeleton/LoadingSkeleton';
import { useAttachSuggestion } from '@jbhi-fi/components/src/hooks/useAttachSuggestion/useAttachSuggestion';
import { useQuery } from 'react-query';
import { Slideout } from '@jbhi-fi/components/src/Slideout/Slideout';
import { ShopifyProductActionsProvider } from '../../../contexts/ShopifyProductActionsProvider';
import { TrackingProvider, useTrackingContext } from '@jbhi-fi/components/src/Tracking';
import { IdPrefixProvider } from '@jbhi-fi/components/src/IdPrefix';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { trackCheckoutTriggered } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackCheckoutTriggered';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { useCouponContext } from '../../../contexts/CouponContextProvider';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';

export interface AttachAddToCartSlideoutProps {
  purchasedProduct: AlgoliaProductHit;
  freeBonusProducts?: Array<AlgoliaProductHit>;
  isOpen?: boolean;
  pageType: string;
  onClose: () => void;
  shouldFetchServiceSuggestions?: boolean;
}

export const AttachAddToCartSlideout = ({
  purchasedProduct,
  onClose,
  pageType,
  isOpen = false,
  shouldFetchServiceSuggestions,
}: AttachAddToCartSlideoutProps) => {
  const attachConfig = window.Jbhifi?.attachConfig;
  const attachSmartModuleSettings = window?.smartModuleSettings.productAttach;
  const attachSlideoutModuleOrder = attachConfig?.slideoutModuleOrder ?? ['services', 'products'];
  const purchasedProductSku = purchasedProduct.sku || purchasedProduct?.product?.id || '';
  const purchasedProductGroupId = purchasedProduct.productGroup?.id.toString() || '';
  const isMarketplaceProduct = AlgoliaHitTransformer.isMarketplaceProduct(purchasedProduct);

  const isPreOrder: boolean = purchasedProduct?.product?.productFlags?.some(flag => flag.Name === 'Pre Order') ?? false;
  const isPreOrderClickAndCollect: boolean =
    purchasedProduct?.product?.productFlags?.some(flag => flag.Name === 'Pre Order Click & Collect') ?? false;
  const isPreOrderProduct: boolean =
    purchasedProduct.availability.canPreOrder || isPreOrder || isPreOrderClickAndCollect;

  const isEnhancedGoogleRecommendationsEnabled = useFeatureFlag('isEnhancedGoogleRecommendationsEnabled');

  const { isCouponSurfacingEnabled, couponsWithExclusions, handleSelectCoupon } = useCouponContext({
    location: 'slideout',
    product: {
      sku: purchasedProductSku,
      productGroupId: purchasedProductGroupId,
      supplierNumber: purchasedProduct.product?.supplierProductDetails?.[0]?.supplierNumber.toString() || '',
      isPreOrder: isPreOrderProduct,
      isMarketplace: isMarketplaceProduct,
      departmentCode: purchasedProduct.product?.departmentCode?.toString() || '',
    },
  });
  const { isPerks } = useCustomerContext();

  const { cart } = useCartContext();
  const { tracker } = useTrackingContext();

  const {
    attachHeading,
    productBlockHeading,
    isLoading: isAttachSuggestionLoading,
  } = useAttachSuggestion({
    purchasedCategoryId: purchasedProduct?.category_id,
  });
  const {
    groupedSuggestions,
    suggestions,
    isLoading: isSuggestedProductsLoading,
  } = useSuggestedProducts({
    products: [purchasedProduct],
    googleRecNumProducts: attachSmartModuleSettings?.productCount || 12,
    googleRecModel: attachSmartModuleSettings?.googleRecModelType || 'FBT',
    googleRecAcceptedDeviationPercentage: window.smartModuleSettings.googleAcceptedDeviationPercentage,
    excludedSkus: cart?.items.map(item => item.sku),
    shouldEnhanceSuggestions: !!isEnhancedGoogleRecommendationsEnabled,
  });

  const variant = purchasedProduct
    ? [
        {
          Sku: parseInt(purchasedProductSku),
          DisplayPriceInc: purchasedProduct.pricing?.displayPriceInc,
        },
      ]
    : undefined;
  const { data: serviceSuggestions = [], isLoading: isServicesLoading } = useQuery({
    queryKey: ['serviceSuggestions', variant],
    queryFn: () => (variant ? fetchServiceSuggestions(variant) : undefined),
    enabled: !isMarketplaceProduct && shouldFetchServiceSuggestions,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const isLoading = isAttachSuggestionLoading || isSuggestedProductsLoading || isServicesLoading;
  const isProductsModuleDisabled =
    attachConfig?.excludedSkus?.includes(purchasedProductSku) ||
    attachConfig?.excludedGroupIds?.includes(purchasedProductGroupId);

  const hasProductsOrServices =
    Boolean(serviceSuggestions?.[0]?.services?.length) ||
    (!isProductsModuleDisabled && (Boolean(groupedSuggestions?.length) || Boolean(suggestions?.length)));

  const cartProduct = cart?.items?.find(
    item => item.sku === purchasedProductSku && item.line_price / 100 === purchasedProduct.price
  );

  const productCartId = cartProduct?.properties?._uuid;

  const preSelectedOfferingId = getSelectedServiceSuggestion()?.selectedOfferingId ?? null;

  const handleCheckoutClick = () => {
    tracker.trackClick({ label: 'Checkout' });
    trackCheckoutTriggered({ triggerSource: 'attach_slideout' });
    window.location.href = '/checkout';
  };

  const handleReviewCartClick = () => {
    tracker.trackClick({ label: 'Review cart' });
    window.location.href = '/cart';
  };

  const servicesModule = isServicesLoading ? (
    <LoadingSkeleton.Box height={{ xs: '320px', md: '220px' }} />
  ) : serviceSuggestions?.[0]?.services?.length && purchasedProductSku ? (
    <SlideoutLayoutSection>
      <ProductService
        key={`${purchasedProductSku}-${isOpen}-${preSelectedOfferingId}`}
        productSku={purchasedProductSku}
        productCartId={productCartId}
        preSelectedOfferingId={preSelectedOfferingId}
        gtmData={purchasedProduct.google_tag_manager}
        suggestions={serviceSuggestions}
        variant="slideout"
      />
    </SlideoutLayoutSection>
  ) : null;

  const productsModule =
    !isProductsModuleDisabled && (groupedSuggestions?.length || suggestions?.length) ? (
      <SlideoutLayoutSection heading={productBlockHeading ?? attachConfig?.productsHeading}>
        <SlideoutProductsModule
          suggestions={suggestions ?? []}
          numProductsPerCategory={attachConfig?.numProductsPerCategory}
          numProductsPerNonBundleCategory={attachConfig?.numProductsPerNonBundleCategory}
          groupedSuggestions={
            groupedSuggestions?.map(({ collection, products }) => ({
              heading: collection?.fields?.title ?? '',
              infoModuleProps: collection?.fields?.attachInfo?.fields
                ? AttachInfoTransformer.toInfoModuleProps(collection.fields.attachInfo)
                : undefined,
              bundleModuleProps: collection?.fields?.attachBundle?.fields
                ? AttachBundleTransformer.toBundleModuleProps(collection.fields.attachBundle)
                : undefined,
              products,
            })) ?? []
          }
          cardVariant={{
            xs: attachConfig.productCardVariantMobile,
            md: attachConfig.productCardVariantDesktop,
          }}
          trackingData={{
            listName: `recommendations-popup${pageType}-${
              attachSmartModuleSettings?.googleRecModelType?.toUpperCase() ?? 'FBT'
            }`,
            pageType,
            group: 'googleRelated',
            category: attachSmartModuleSettings.type ?? 'frequentlyBoughtTogether',
          }}
        />
      </SlideoutLayoutSection>
    ) : null;

  const slideoutModules = {
    services: servicesModule,
    products: productsModule,
  };

  return (
    <ShopifyProductActionsProvider>
      <TrackingProvider category="attach" context="slideout">
        <IdPrefixProvider prefix="slideout">
          <Slideout
            isOpen={Boolean(isOpen && hasProductsOrServices && !isLoading)}
            onClose={() => {
              tracker.trackClick({ label: 'Slideout close' });
              onClose();
            }}
            minWidth="800px"
            dataTestId="attach-slideout"
          >
            <SlideoutLayout
              heading={attachHeading ?? window.Jbhifi?.attachConfig?.attachHeading ?? ''}
              product={purchasedProduct}
              freeBonusProducts={window.Jbhifi.bonusProducts ?? []}
              onCheckoutClick={handleCheckoutClick}
              onReviewCartClick={handleReviewCartClick}
              onContinueShoppingClick={() => {
                tracker.trackClick({ label: 'Continue shopping' });
                onClose();
              }}
              isCouponSurfacingEnabled={isCouponSurfacingEnabled}
              coupons={couponsWithExclusions}
              handleSelectCoupon={handleSelectCoupon}
              isPerks={isPerks}
            >
              {attachSlideoutModuleOrder.map(module => (
                <React.Fragment key={module}>{slideoutModules[module]}</React.Fragment>
              ))}
            </SlideoutLayout>
          </Slideout>
        </IdPrefixProvider>
      </TrackingProvider>
    </ShopifyProductActionsProvider>
  );
};
